// src/admin/pages/AdminSeoManager.js
import React, { useState, useEffect, useContext } from "react";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaSave, FaImage } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { getSeoByPage, saveSeoByPage } from "../../services/adminSeo.api";
import { getPublicCategories } from "../../services/categories.api";

function AdminSeoManager() {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const { lang } = useContext(AdminLanguageContext);

  const ACTIVE_SEO_PAGES = ["home", "products", "product", "category"];

  const TEXT = {
    EN: {
      title: "SEO Manager",
      choosePage: "Choose a Page",
      chooseCategory: "Choose a Category",
      loading: "Loading SEO data...",
      metaTitle: "Meta Title",
      metaDesc: "Meta Description",
      keywords: "Keywords (comma separated)",
      canonical: "Canonical URL",
      indexable: "Allow Indexing (index/follow)",
      ogTitle: "OG Title",
      ogDesc: "OG Description",
      ogImage: "OG Image",
      jsonld: "JSON-LD Structured Data",
      save: "Save SEO Settings",
      pages: {
        home: "Home Page",
        about: "About Us",
        contact: "Contact Us",
        faq: "FAQ",
        products: "Products Listing",
        product: "Single Product Page",
        category: "Category Page",
      },
    },
    AR: {
      title: "إدارة السيو",
      choosePage: "اختر الصفحة",
      chooseCategory: "اختر التصنيف",
      loading: "جاري تحميل بيانات السيو...",
      metaTitle: "عنوان الميتا",
      metaDesc: "وصف الميتا",
      keywords: "الكلمات المفتاحية (مفصولة بفاصلة)",
      canonical: "الرابط الأساسي (Canonical)",
      indexable: "السماح بالأرشفة (index/follow)",
      ogTitle: "عنوان OG",
      ogDesc: "وصف OG",
      ogImage: "صورة OG",
      jsonld: "بيانات JSON-LD المنظمة",
      save: "حفظ إعدادات السيو",
      pages: {
        home: "الصفحة الرئيسية",
        about: "من نحن",
        contact: "اتصل بنا",
        faq: "الأسئلة الشائعة",
        products: "قائمة المنتجات",
        product: "صفحة المنتج",
        category: "صفحة التصنيف",
      },
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
const isAdmin = adminUser.role === "admin";

const canAccess = isAdmin || perms.can_manage_seo;


  const [page, setPage] = useState("home");

  // ✅ categories for category SEO
  const [categories, setCategories] = useState([]);
  const [categorySlug, setCategorySlug] = useState("");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");
  const [canonical, setCanonical] = useState("");
  const [indexable, setIndexable] = useState(true);

  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDescription] = useState("");
  const [ogImage, setOgImage] = useState(null);
  const [jsonLd, setJsonLd] = useState("");

  // ✅ load categories once (for category SEO)
  useEffect(() => {
    getPublicCategories()
      .then((rows) => {
        const list = rows || [];
        setCategories(list);
        // if first time, set default slug
        if (!categorySlug && list.length) {
          setCategorySlug(list[0].slug);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine entityId for current page
  const entityId = page === "category" ? categorySlug || null : null;

  // ✅ load SEO when page changes OR category slug changes (if category)
  useEffect(() => {
    setLoading(true);

    getSeoByPage(page, entityId)
      .then((data) => {
        // reset fields if no data
        if (!data) {
          setTitle("");
          setDescription("");
          setKeywords("");
          setCanonical("");
          setIndexable(true);
          setOgTitle("");
          setOgDescription("");
          setOgImage(null);
          setJsonLd("");
          return;
        }

        setTitle(data.title || "");
        setDescription(data.meta_description || "");
        setKeywords(data.meta_keywords || "");
        setCanonical(data.canonical || "");
        setIndexable(!!data.is_indexable);
        setOgTitle(data.title || "");
        setOgDescription(data.meta_description || "");
        setJsonLd(data.json_ld || "");
      })
      .finally(() => setLoading(false));
  }, [page, entityId]);

  const handleSave = async () => {
    await saveSeoByPage(page, entityId, {
      title,
      meta_description: description,
      meta_keywords: keywords,
      canonical,
      og_image_url: ogImage,
      is_indexable: indexable,
      json_ld: jsonLd,
    });

    alert("SEO settings saved");
  };

  const categoryLabel = (c) => (lang === "EN" ? c.name_en : c.name_ar);

  return (
    <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="flex-1 lg:ml-64 transition-all duration-300">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 animate-fadeSlide max-w-4xl mx-auto">
        {/* 🚫 Mobile block message */}
<div className="block md:hidden">
  <div className="min-h-[60vh] flex items-center justify-center px-6">
    <div className="bg-white border rounded-2xl p-6 text-center shadow max-w-sm">
      <h2 className="text-lg font-bold text-gray-800 mb-2">
        {lang === "AR"
          ? "هذه الميزة متاحة فقط على أجهزة الكمبيوتر"
          : "This feature is available only on desktop"}
      </h2>
      <p className="text-gray-500 text-sm">
        {lang === "AR"
          ? "يرجى استخدام جهاز لوحي أو كمبيوتر للوصول إلى هذه الصفحة"
          : "Please use a tablet or desktop device to access this page"}
      </p>
    </div>
  </div>
</div>
<div className="hidden md:block">
  {!canAccess ? (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700 font-semibold">
        {lang === "AR"
          ? "غير مصرح لك بإدارة إعدادات SEO"
          : "You are not allowed to manage SEO settings"}
      </p>
    </div>
  ) : (
    <>
          <h1 className="text-3xl font-bold text-cyan-700 mb-6">{TEXT.title}</h1>

          <div className="mb-6">
            <label className="font-semibold text-gray-700">{TEXT.choosePage}</label>
            <select
              className="w-full p-3 border rounded-xl shadow-sm mt-2 focus:ring-2 focus:ring-cyan-400"
              value={page}
              onChange={(e) => setPage(e.target.value)}
            >
              {Object.entries(TEXT.pages)
                .filter(([key]) => ACTIVE_SEO_PAGES.includes(key))
                .map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
            </select>
          </div>

          {/* ✅ Category selector appears ONLY for category page */}
          {page === "category" && (
            <div className="mb-6">
              <label className="font-semibold text-gray-700">{TEXT.chooseCategory}</label>
              <select
                className="w-full p-3 border rounded-xl shadow-sm mt-2 focus:ring-2 focus:ring-cyan-400"
                value={categorySlug}
                onChange={(e) => setCategorySlug(e.target.value)}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.slug}>
                    {categoryLabel(c)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loading ? (
            <p className="text-gray-600">{TEXT.loading}</p>
          ) : (
            <div className="bg-white shadow-xl rounded-2xl p-6 space-y-6 border border-gray-200">
              <div>
                <label className="font-semibold">{TEXT.metaTitle}</label>
                <input
                  type="text"
                  className="w-full p-3 border rounded-xl mt-1"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div>
                <label className="font-semibold">{TEXT.metaDesc}</label>
                <textarea
                  rows={3}
                  className="w-full p-3 border rounded-xl mt-1"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div>
                <label className="font-semibold">{TEXT.keywords}</label>
                <input
                  type="text"
                  className="w-full p-3 border rounded-xl mt-1"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                />
              </div>

              <div>
                <label className="font-semibold">{TEXT.canonical}</label>
                <input
                  type="text"
                  className="w-full p-3 border rounded-xl mt-1"
                  value={canonical}
                  onChange={(e) => setCanonical(e.target.value)}
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={indexable}
                  onChange={(e) => setIndexable(e.target.checked)}
                />
                <label className="font-semibold">{TEXT.indexable}</label>
              </div>

              <div>
                <label className="font-semibold">{TEXT.ogTitle}</label>
                <input
                  type="text"
                  className="w-full p-3 border rounded-xl mt-1"
                  value={ogTitle}
                  onChange={(e) => setOgTitle(e.target.value)}
                />
              </div>

              <div>
                <label className="font-semibold">{TEXT.ogDesc}</label>
                <textarea
                  rows={3}
                  className="w-full p-3 border rounded-xl mt-1"
                  value={ogDescription}
                  onChange={(e) => setOgDescription(e.target.value)}
                />
              </div>

              <div>
                <label className="font-semibold">{TEXT.ogImage}</label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setOgImage(e.target.files[0])}
                    className="p-2 border rounded-xl"
                  />
                  {ogImage && (
                    <div className="bg-gray-100 border p-2 rounded-xl">
                      <FaImage className="text-cyan-600 text-xl" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="font-semibold">{TEXT.jsonld}</label>
                <textarea
                  rows={5}
                  className="w-full p-3 border rounded-xl mt-1 font-mono"
                  placeholder={`{\n  "@context": "https://schema.org",\n  ...\n}`}
                  value={jsonLd}
                  onChange={(e) => setJsonLd(e.target.value)}
                />
              </div>

              <button
                onClick={handleSave}
                className="bg-cyan-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 hover:bg-cyan-700 shadow"
              >
                <FaSave /> {TEXT.save}
              </button>
            </div>
          )}
            </>
  )}
</div>
</main>

      </div>
    </div>
  );
}

export default AdminSeoManager;
